import React, { useEffect, useState } from 'react';
import { Radar, Flame, MapPin, Clock } from 'lucide-react';
import { affFetch } from '../../lib/affApi';

const HEAT = {
  hot: 'bg-red-50 text-red-600 border-red-200',
  warm: 'bg-amber-50 text-amber-700 border-amber-200',
  scan: 'bg-sky-50 text-sky-700 border-sky-200',
};

const fmt = (n) => '$' + Number(n).toLocaleString('en-US');

const localTime = (tz) => {
  try {
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date());
  } catch { return null; }
};

export const PartnerRadar = ({ isEs }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    affFetch('/me/radar').then((r) => r.ok && r.json().then(setData)).catch(() => {});
  }, []);
  if (!data) return null;
  const c = isEs
    ? { title: 'Radar de leads en vivo', sub: 'Cada restaurante que entra por tu enlace y usa el Score™ o calcula su ROI aparece aquí. Los calientes primero.',
        hot: 'CALIENTE — calculó su ROI', warm: 'TIBIO — desbloqueó su informe', scan: 'Escaneó su Score',
        empty: 'Aún no hay actividad en tu radar. Comparte tu enlace del Score™ — cada escaneo aparecerá aquí en tiempo real.',
        call: '¡Llámalo ya!', potential: 'potencial' }
    : { title: 'Live lead radar', sub: 'Every restaurant that comes through your link and runs the Score™ or calculates its ROI shows up here. Hot ones first.',
        hot: 'HOT — calculated their ROI', warm: 'WARM — unlocked their report', scan: 'Ran their Score',
        empty: 'No radar activity yet. Share your Score™ link — every scan will appear here in real time.',
        call: 'Call them now!', potential: 'potential' };
  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="aff-radar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#F77F11] font-bold">
          <Radar className="w-4 h-4" /> {c.title}
        </p>
        {data.hot_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-200" data-testid="aff-radar-hot-count">
            <Flame className="w-3 h-3" /> {data.hot_count} {isEs ? 'caliente(s)' : 'hot'}
          </span>
        )}
      </div>
      <p className="mt-1 text-[12px] text-neutral-500">{c.sub}</p>
      {data.items.length === 0 ? (
        <p className="mt-4 text-[13px] text-neutral-500">{c.empty}</p>
      ) : (
        <div className="mt-4 space-y-2">
          {data.items.slice(0, 10).map((it, i) => (
            <div key={i} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${it.heat === 'hot' ? 'border-red-200 bg-red-50/50' : 'border-neutral-200 bg-neutral-50'}`} data-testid={`aff-radar-item-${i}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="text-[13.5px] font-semibold text-neutral-900">{it.restaurant}{it.city ? <span className="text-neutral-500 font-normal"> · {it.city}</span> : null}</p>
                <p className="text-[11px] text-neutral-500">
                  {c[it.heat]}{it.overall != null ? ` · Score ${it.overall}/100` : ''}{it.email_masked ? ` · ${it.email_masked}` : ''}
                </p>
                {(it.geo_city || it.tz) && (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-neutral-600 font-medium" data-testid={`aff-radar-geo-${i}`}>
                    {it.geo_city && (
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-[#F77F11]" />{[it.geo_city, it.geo_region, it.geo_cc].filter(Boolean).join(', ')}</span>
                    )}
                    {it.tz && localTime(it.tz) && (
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-[#F77F11]" />{localTime(it.tz)} {isEs ? 'hora local' : 'local time'}</span>
                    )}
                  </p>
                )}
              </div>
              {it.roi_monthly_low != null && (
                <p className="text-[13px] font-bold text-[#F77F11] tabular-nums">
                  {fmt(it.roi_monthly_low)}–{fmt(it.roi_monthly_high)}/mo {c.potential}
                </p>
              )}
              <span className={`text-[10px] font-bold uppercase rounded-full border px-2 py-0.5 ${HEAT[it.heat] || HEAT.scan}`}>
                {it.heat === 'hot' ? `🔥 ${c.call}` : it.heat}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
